import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import {
  QuestionLayout,
  IconInput,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useNavigation } from "@react-navigation/native";
import { MoreHorizontal } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { MedicationsVaccinesStackScreenProps } from "../MedicationsVaccinesNavigator";
import {
  VaccineType,
  useMedicationsVaccines,
} from "../MedicationsVaccinesContext";
import { VACCINE_OPTIONS } from "../options";

export function VaccinesSelectScreen() {
  const t = useTranslations();
  const { state, updateState } = useMedicationsVaccines();
  const navigation =
    useNavigation<
      MedicationsVaccinesStackScreenProps<"VaccinesSelect">["navigation"]
    >();
  const selected = useMemo(
    () => new Set(state.selectedVaccines || []),
    [state.selectedVaccines],
  );

  const toggleSelection = (vaccine: VaccineType | "none") => {
    const newSelection = new Set(selected);
    if (newSelection.has(vaccine)) {
      newSelection.delete(vaccine);
    } else {
      newSelection.add(vaccine);
    }

    const arr = Array.from(newSelection) as (VaccineType | "none")[];
    if (arr.includes("none")) {
      updateState({ selectedVaccines: ["none"] });
    } else {
      updateState({
        selectedVaccines: arr.filter((v) => v !== "none") as VaccineType[],
      });
    }
  };

  const handleSave = () => {
    const selectedArray = Array.from(selected);
    if (selectedArray.includes("none")) {
      // If "none" selected, navigate to complete screen
      navigation.navigate("MedicationsVaccinesComplete");
      return;
    }

    // If vaccines selected, go to details screen
    navigation.navigate("VaccineDetails");
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.medications_vaccines.vaccines_title")}
      subtitle={t("med_history.medical.medications_vaccines.vaccines_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0}
      saveLabel={t("med_history.medical.shared.continue")}
    >
      <View style={styles.optionsGrid}>
        {VACCINE_OPTIONS.map((option) => {
          const isSelected = selected.has(option.id);
          return (
            <View key={option.id} style={styles.optionWrapper}>
              <SquareOptionButton
                emoji={option.emoji}
                label={t(option.labelKey)}
                isSelected={isSelected}
                onPress={() => toggleSelection(option.id)}
                labelSize="sm"
                style={styles.optionButton}
              />
            </View>
          );
        })}
      </View>
      {selected.has("other") && !selected.has("none") && (
        <View style={styles.otherInput}>
          <IconInput
            icon={MoreHorizontal}
            label={t(
              "med_history.medical.medications_vaccines.other_vaccine_label",
            )}
            placeholder={t(
              "med_history.medical.medications_vaccines.other_vaccine_placeholder",
            )}
            value={state.otherVaccineText}
            onChangeText={(text) => updateState({ otherVaccineText: text })}
          />
        </View>
      )}
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
  optionButton: {
    minHeight: 160,
    padding: 24,
    borderRadius: 16,
  },
  otherInput: {
    width: "100%",
    marginTop: -28,
    marginBottom: 40,
  },
});

export default VaccinesSelectScreen;
